import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Plus, Trash2, Image as ImageIcon, Upload, Star, Video, FileText } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import ImageSuggestionModal from '../../../components/admin/ImageSuggestionModal';
import BunnyLibraryModal from '../../../components/admin/BunnyLibraryModal';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import { uploadImage } from '../../../services/imageService';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectsForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    label_pt: '', label_en: '', heading_pt: '', heading_en: '', projects: []
  });

  useEffect(() => { loadProjectsData(); }, []);

  const loadProjectsData = async () => {
    setLoading(true);
    try {
      // Load RAW data to get both _pt and _en fields
      const response = await axios.get(`${API_URL}/api/homepage-ml/projects?lang=raw`);
      const data = response.data;
      setFormData({
        label_pt: data.label_pt || '', label_en: data.label_en || '',
        heading_pt: data.heading_pt || '', heading_en: data.heading_en || '',
        projects: (data.projects || []).map((p, idx) => ({
          id: p.id || idx + 1,
          featured: p.featured || false,
          title_pt: p.title_pt || '', title_en: p.title_en || '',
          location_pt: p.location_pt || '', location_en: p.location_en || '',
          type_pt: p.type_pt || '', type_en: p.type_en || '',
          description_pt: p.description_pt || '', description_en: p.description_en || '',
          image: p.image || '',
          video_url: p.video_url || '',
          link: p.link || '',
          case_study_pt: p.case_study_pt || '', case_study_en: p.case_study_en || '',
          highlights: p.highlights || []
        }))
      });
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleProjectChange = (index, field, value) => {
    setFormData(prev => {
      const p = [...prev.projects];
      p[index] = { ...p[index], [field]: value };
      return { ...prev, projects: p };
    });
  };

  const addProject = () => {
    setFormData(prev => ({ ...prev, projects: [...prev.projects, {
      id: prev.projects.length + 1, featured: false,
      title_pt: '', title_en: '', location_pt: '', location_en: '',
      type_pt: '', type_en: '', description_pt: '', description_en: '',
      image: '', video_url: '', link: '', case_study_pt: '', case_study_en: '', highlights: []
    }] }));
  };

  const removeProject = (index) => setFormData(prev => ({ ...prev, projects: prev.projects.filter((_, i) => i !== index) }));

  const handleImageSelect = (url) => {
    if (currentProjectIndex !== null) handleProjectChange(currentProjectIndex, 'image', url);
  };

  const handleFileUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const result = await uploadImage(file, 'images/projects');
      if (result.success) handleProjectChange(index, 'image', result.data.cdn_url);
    } catch (error) { console.error(error); }
    setUploading(false);
  };

  const addHighlight = (index) => {
    const p = [...formData.projects];
    p[index].highlights = [...(p[index].highlights || []), { value_pt: '', value_en: '', label_pt: '', label_en: '' }];
    setFormData(prev => ({ ...prev, projects: p }));
  };

  const handleHighlightChange = (pIdx, hIdx, field, value) => {
    const p = [...formData.projects];
    p[pIdx].highlights[hIdx] = { ...p[pIdx].highlights[hIdx], [field]: value };
    setFormData(prev => ({ ...prev, projects: p }));
  };

  const removeHighlight = (pIdx, hIdx) => {
    const p = [...formData.projects];
    p[pIdx].highlights = p[pIdx].highlights.filter((_, i) => i !== hIdx);
    setFormData(prev => ({ ...prev, projects: p }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/homepage-ml/projects`, formData);
      toast({ title: "Sucesso", description: "Projects guardados" });
    } catch (error) {
      toast({ variant: "destructive", title: "Erro", description: "Falha ao guardar" });
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-5xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Projects & Case Studies</CardTitle>
            <CardDescription>Manage featured projects, case studies, video links and highlights</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Section Header */}
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Label PT</label><input value={formData.label_pt} onChange={(e) => handleInputChange('label_pt', e.target.value)} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Label EN</label><input value={formData.label_en} onChange={(e) => handleInputChange('label_en', e.target.value)} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Heading PT</label><input value={formData.heading_pt} onChange={(e) => handleInputChange('heading_pt', e.target.value)} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Heading EN</label><input value={formData.heading_en} onChange={(e) => handleInputChange('heading_en', e.target.value)} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>

              {/* Projects */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Projects ({formData.projects.length})</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addProject}><Plus className="w-4 h-4 mr-2" /> Add Project</Button>
                </div>

                {formData.projects.map((project, idx) => (
                  <Card key={idx} className={`p-5 mb-6 ${project.featured ? 'border-2 border-[#FF6B35]' : ''}`}>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-3">
                        <h4 className="font-bold text-lg">Project {idx + 1}</h4>
                        {project.featured && <span className="bg-[#FF6B35] text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"><Star className="w-3 h-3 fill-white" /> Featured</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant={project.featured ? "default" : "outline"} size="sm"
                          className={project.featured ? "bg-[#FF6B35]" : ""}
                          onClick={() => handleProjectChange(idx, 'featured', !project.featured)}>
                          <Star className="w-4 h-4 mr-1" /> {project.featured ? 'Featured' : 'Set Featured'}
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => removeProject(idx)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>

                    {/* Image */}
                    {project.image && (
                      <div className="relative mb-3">
                        <img src={project.image} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                        <Button type="button" variant="destructive" size="sm" onClick={() => handleProjectChange(idx, 'image', '')} className="absolute top-2 right-2">Remover</Button>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div><input type="file" id={`proj-upload-${idx}`} accept="image/*" onChange={(e) => handleFileUpload(e, idx)} className="hidden" />
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => document.getElementById(`proj-upload-${idx}`).click()}><Upload className="w-4 h-4 mr-1" /> Upload</Button></div>
                      <Button type="button" variant="outline" size="sm" className="w-full border-[#00B4D8] text-[#00B4D8]" onClick={() => { setCurrentProjectIndex(idx); setLibraryOpen(true); }}><ImageIcon className="w-4 h-4 mr-1" /> Biblioteca</Button>
                      <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => { setCurrentProjectIndex(idx); setSuggestionsOpen(true); }}><ImageIcon className="w-4 h-4 mr-1" /> Sugestões</Button>
                    </div>

                    {/* Basic Fields */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div><label className="text-xs text-gray-500">Title PT</label><input value={project.title_pt} onChange={(e) => handleProjectChange(idx, 'title_pt', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Title EN</label><input value={project.title_en} onChange={(e) => handleProjectChange(idx, 'title_en', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Location PT</label><input value={project.location_pt} onChange={(e) => handleProjectChange(idx, 'location_pt', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Location EN</label><input value={project.location_en} onChange={(e) => handleProjectChange(idx, 'location_en', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Type PT</label><input value={project.type_pt} onChange={(e) => handleProjectChange(idx, 'type_pt', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Type EN</label><input value={project.type_en} onChange={(e) => handleProjectChange(idx, 'type_en', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" /></div>
                    </div>

                    {/* Description */}
                    <div className="space-y-3 mb-4">
                      <div><label className="text-xs text-gray-500">Description PT</label><textarea value={project.description_pt} onChange={(e) => handleProjectChange(idx, 'description_pt', e.target.value)} rows={3} className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Description EN</label><textarea value={project.description_en} onChange={(e) => handleProjectChange(idx, 'description_en', e.target.value)} rows={3} className="w-full px-3 py-2 border rounded text-sm" /></div>
                    </div>

                    {/* Video & Link */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div><label className="text-xs text-gray-500 flex items-center gap-1"><Video className="w-3 h-3" /> Video URL (YouTube)</label><input value={project.video_url} onChange={(e) => handleProjectChange(idx, 'video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full px-3 py-2 border rounded text-sm" /></div>
                      <div><label className="text-xs text-gray-500">Link</label><input value={project.link} onChange={(e) => handleProjectChange(idx, 'link', e.target.value)} placeholder="/projects/slug" className="w-full px-3 py-2 border rounded text-sm" /></div>
                    </div>

                    {/* Case Study (Full Story) */}
                    <div className="space-y-3 mb-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-[#023E8A]" /><label className="text-sm font-semibold text-[#023E8A]">Full Case Study</label></div>
                      <div><label className="text-xs text-gray-500">Case Study PT (Full Story)</label><textarea value={project.case_study_pt} onChange={(e) => handleProjectChange(idx, 'case_study_pt', e.target.value)} rows={5} className="w-full px-3 py-2 border rounded text-sm" placeholder="Write the full project story in Portuguese..." /></div>
                      <div><label className="text-xs text-gray-500">Case Study EN (Full Story)</label><textarea value={project.case_study_en} onChange={(e) => handleProjectChange(idx, 'case_study_en', e.target.value)} rows={5} className="w-full px-3 py-2 border rounded text-sm" placeholder="Write the full project story in English..." /></div>
                    </div>

                    {/* Highlights/KPIs */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <label className="text-sm font-semibold">Highlights / KPIs</label>
                        <Button type="button" variant="outline" size="sm" onClick={() => addHighlight(idx)}><Plus className="w-3 h-3 mr-1" /> Add KPI</Button>
                      </div>
                      {(project.highlights || []).map((h, hIdx) => (
                        <div key={hIdx} className="grid grid-cols-5 gap-2 mb-2 items-center">
                          <input value={h.value_pt || h.value || ''} onChange={(e) => handleHighlightChange(idx, hIdx, 'value_pt', e.target.value)} placeholder="Value PT" className="px-2 py-1 border rounded text-sm" />
                          <input value={h.value_en || h.value || ''} onChange={(e) => handleHighlightChange(idx, hIdx, 'value_en', e.target.value)} placeholder="Value EN" className="px-2 py-1 border rounded text-sm" />
                          <input value={h.label_pt || h.label || ''} onChange={(e) => handleHighlightChange(idx, hIdx, 'label_pt', e.target.value)} placeholder="Label PT" className="px-2 py-1 border rounded text-sm" />
                          <input value={h.label_en || h.label || ''} onChange={(e) => handleHighlightChange(idx, hIdx, 'label_en', e.target.value)} placeholder="Label EN" className="px-2 py-1 border rounded text-sm" />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeHighlight(idx, hIdx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>

              {/* Actions */}
              <div className="space-y-4 pt-6 border-t">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancel</Button>
                  <Button type="submit" disabled={saving} className="bg-[#00B4D8]">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Projects</>}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <ImageSuggestionModal category="projects" isOpen={suggestionsOpen} onClose={() => setSuggestionsOpen(false)} onSelect={handleImageSelect} />
      <BunnyLibraryModal folder="projects" isOpen={libraryOpen} onClose={() => setLibraryOpen(false)} onSelect={handleImageSelect} />
    </div>
  );
};

export default ProjectsForm;
